import { faFileWord, faPlus } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, TableProps } from 'antd';
import DOMPurify from 'dompurify';
import React, { useMemo, useState } from 'react';
import { useParams } from 'react-router';

import PharmaReportIcon from '../../../foundation/assets/svgs/PharmaReportIcon';
import ResizableTable from '../../../foundation/components/resizable_table/ResizableTable';
import { useFileDownload } from '../../../foundation/utils/fileDownload';
import {
  useCreateReportForSampleMutation,
  useGetVariantSamplesQuery,
  usePrepareMolgenReportMutation,
} from '../../api/variantApi';
import LinkedVariantsTooltip from '../../probands_samples/LinkedVariantsTooltip';
import LoadMoreBtn from '../components/LoadMoreBtn';
import { VARIANT_STAGE_TYPES } from '../constants';
import { useVariantStep } from '../hooks/use_variant_step';
import { useHasRunningRefresh } from '../hooks/useHasRunningRefresh';
import { useVariantAccess } from '../hooks/useVariantAccess';
import { VariantProbandReportData, VariantSample } from '../redux';
import MedicalInvestigationSelectModal from './MedicalInvestigationSelectModal';
import ReportCategoryModal from './ReportCategoryModal';
import ReportPanelModal from './ReportPanelModal';
import SampleEditModal from './SampleEditModal';
import SampleModal from './SampleModal';
import SegregationModal from './SegregationModal';
import SubjectModal from './SubjectModal';
import ZygosityModal from './ZygosityModal'; // ZygosityModal import

const LOAD_MORE_CUTTOFF_LENGTH = 3;

const SamplesSection = () => {
  const params = useParams();

  const [isSampleModalVisible, setSampleModalVisibility] = useState(false);
  const [isReportPanelModalVisible, setReportPanelModalVisibility] =
    useState(false);
  const [isSampleEditModalVisible, setSampleEditModalVisibility] =
    useState(false);
  const [isSubjectModalVisible, setSubjectModalVisibility] = useState(false);
  const [isReportCategoryModalVisible, setReportCategoryModalVisibility] =
    useState(false);
  const [isZygosityModalVisible, setZygosityModalVisibility] = useState(false);
  const [isSegregationModalVisible, setSegregationModalVisibility] =
    useState(false);
  const [isMISelectVisible, setMISelectVisible] = useState(false);
  const [pendingReportType, setPendingReportType] = useState<
    'molgen' | 'pharma' | undefined
  >(undefined);
  const { downloadFile } = useFileDownload();
  const [prepareMolgenReport] = usePrepareMolgenReportMutation();
  const [createReportForSample] = useCreateReportForSampleMutation();
  const [reportData, setReportData] = useState<VariantProbandReportData>();
  const [selectedRecord, setSelectedRecord] = useState<VariantSample>();
  const [showMore, setShowMore] = useState(false);
  const { canEditWorkflow } = useVariantAccess();
  const hasRunningRefresh = useHasRunningRefresh(params.vid);

  const stageInfo = useVariantStep(VARIANT_STAGE_TYPES.CLINICAL_OBSERVATION);

  // Read samples straight from the query hook's cache rather than a selector
  const { data: dataSource, isLoading: isSamplesLoading } =
    useGetVariantSamplesQuery(
      { variant_id: params.vid },
      { skip: !params.vid },
    );
  const samples = dataSource ?? [];

  const handleReportCategoryClick = (
    reportData: VariantProbandReportData,
    data: VariantSample,
  ) => {
    setReportData(reportData);
    setReportCategoryModalVisibility(true);
    setSelectedRecord(data);
  };

  const handleSampleEditModalClose = () => {
    setSampleEditModalVisibility(false);
    setSelectedRecord(undefined);
  };

  const handleSampleEditModalOpen = (
    e: React.MouseEvent<HTMLAnchorElement>,
    record: VariantSample,
  ) => {
    e.preventDefault();
    e.stopPropagation();
    setSampleEditModalVisibility(true);
    setSelectedRecord(record);
  };

  const handleSubjectModalClose = () => {
    setSubjectModalVisibility(false);
  };

  const handleSubjectModalOpen = () => {
    setSubjectModalVisibility(true);
  };

  const handleReportPanelModalOpen = (
    e: React.MouseEvent<HTMLAnchorElement>,
    record: VariantSample,
  ) => {
    e.preventDefault();
    e.stopPropagation();
    setSelectedRecord(record);
    setReportPanelModalVisibility(true);
  };

  const handleZygosityClick = (sample: VariantSample) => {
    setSelectedRecord(sample);
    setZygosityModalVisibility(true);
  };

  const handleZygosityModalClose = () => {
    setZygosityModalVisibility(false);
    setSelectedRecord(undefined);
  };

  const handleSegregationClick = (sample: VariantSample) => {
    setSelectedRecord(sample);
    setSegregationModalVisibility(true);
  };

  const handleSegregationModalClose = () => {
    setSegregationModalVisibility(false);
    setSelectedRecord(undefined);
  };

  const generateReport = async (
    record: VariantSample,
    type: 'molgen' | 'pharma',
    medicalinvestigation_id?: number,
  ) => {
    try {
      // Molgen uses a two-step flow: prepare the report first, then download it.
      // Each step is a separate mutation call returning a promise with `.unwrap()`
      if (type === 'molgen') {
        await prepareMolgenReport({
          sample_id: record.sample_id,
          medicalinvestigation_id,
        }).unwrap();
      }

      // Second step: download the prepared report blob
      const blob = await createReportForSample({
        type,
        sample_id: record.sample_id,
        medicalinvestigation_id,
      }).unwrap();

      const idString =
        medicalinvestigation_id != null
          ? String(medicalinvestigation_id)
          : 'no-id';

      downloadFile({
        filename: `${type}_report_${record.sample_id}_${idString}.docx`,
        blob,
      });

      return true;
    } catch (error) {
      console.log(error);
      return false;
    }
  };

  const handleReportIconClick = (
    record: VariantSample,
    type: 'molgen' | 'pharma',
  ) => {
    const list = record.report_data || [];
    if (list.length > 1) {
      setSelectedRecord(record);
      setPendingReportType(type);
      setMISelectVisible(true);
      return;
    }

    const medicalinvestigation_id = list?.[0]?.medicalinvestigation_id;
    // Fire and forget; generateReport internally handles errors and shows a notification
    void generateReport(record, type, medicalinvestigation_id);
  };

  const columns: TableProps<VariantSample>['columns'] = useMemo(
    () => [
      {
        title: 'Sample id',
        dataIndex: 'sample_external_id',
        key: 'sample_external_id',
        width: 80,
        render: (text, record) => (
          <a
            className="clinical-obs__report-category-link"
            rel="noopener noreferrer"
            onClick={(e) => handleSampleEditModalOpen(e, record)}
          >
            {text}
          </a>
        ),
      },
      {
        title: 'All variants',
        dataIndex: 'genotype_variants',
        key: 'all_variants',
        width: 120,
        render: (_, record) => (
          <LinkedVariantsTooltip
            variants={record.genotype_variants || []}
            showLinks
          />
        ),
      },
      {
        title: 'Indication',
        dataIndex: 'indication',
        key: 'indication',
        width: 180,
        render: (text: string | null | undefined) =>
          text ? (
            <div
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(text) }}
            />
          ) : null,
      },
      {
        title: 'Zygosity',
        dataIndex: 'zygosity',
        key: 'zygosity',
        width: 120,
        render: (text, record) => (
          <a
            className="clinical-obs__report-category-link"
            onClick={() => handleZygosityClick(record)}
          >
            {text}
          </a>
        ),
      },
      {
        title: 'Segregation',
        dataIndex: 'segregation',
        key: 'segregation',
        width: 180,
        render: (text, record) => {
          const displayText = text == null || text === '' ? 'None' : text;
          return (
            <a
              className="clinical-obs__report-category-link"
              onClick={() => handleSegregationClick(record)}
            >
              {displayText}
            </a>
          );
        },
      },
      {
        title: 'Selected diagnostic panel for report',
        dataIndex: 'report_data',
        width: 150,
        key: 'report_data',
        render: (text: [], record: VariantSample) => {
          if (text.length === 0) {
            return (
              <a
                className="clinical-obs__report-category-link"
                onClick={(e) => handleReportPanelModalOpen(e, record)}
              >
                Add report panel
              </a>
            );
          }
          return text.map((item: any) => (
            <div key={item.medicalinvestigation_name}>
              <a
                className="clinical-obs__report-category-link"
                onClick={(e) => handleReportPanelModalOpen(e, record)}
              >
                {item.medicalinvestigation_name}
              </a>
            </div>
          ));
        },
      },
      {
        title: 'Report Category',
        dataIndex: 'report_data',
        width: 150,
        key: 'report_data',
        render: (text: [], record: VariantSample) => {
          return text.map((item: any) => (
            <div key={item.id}>
              <a
                rel="noopener noreferrer"
                className="clinical-obs__report-category-link"
                onClick={() => handleReportCategoryClick(item, record)}
              >
                {item.report_category}
              </a>
            </div>
          ));
        },
      },
      {
        title: 'Generate reports',
        key: 'reports',
        width: 100,
        render: (text, record: VariantSample) => {
          const disabled = !canEditWorkflow;
          const handleMolgenClick = () => {
            if (disabled) {
              return;
            }
            handleReportIconClick(record, 'molgen');
          };
          const handlePharmaClick = () => {
            if (disabled) {
              return;
            }
            handleReportIconClick(record, 'pharma');
          };
          const commonIconStyle = {
            cursor: disabled ? 'not-allowed' : 'pointer',
            opacity: disabled ? 0.5 : 1,
            fontSize: '22px',
          } as React.CSSProperties;
          return (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <span
                role="button"
                aria-disabled={disabled}
                tabIndex={disabled ? -1 : 0}
                onClick={handleMolgenClick}
                style={{ display: 'inline-flex' }}
              >
                <FontAwesomeIcon
                  icon={faFileWord}
                  style={{ ...commonIconStyle, marginLeft: '6px' }}
                />
              </span>
              <span
                role="button"
                aria-disabled={disabled}
                tabIndex={disabled ? -1 : 0}
                onClick={handlePharmaClick}
                style={{ display: 'inline-flex' }}
              >
                <PharmaReportIcon style={commonIconStyle} />
              </span>
            </div>
          );
        },
      },
      {
        title: 'Comments',
        dataIndex: 'comment',
        key: 'comment',
        width: 150,
        render: (text: string | null | undefined) =>
          text ? (
            <div
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(text),
              }}
            />
          ) : null,
      },
    ],
    [
      handleSampleEditModalOpen,
      handleZygosityClick,
      handleSegregationClick,
      handleReportPanelModalOpen,
      handleReportCategoryClick,
    ],
  );

  // unified disable logic for "New sample" analogue to AddReference
  const isNewSampleDisabled =
    !canEditWorkflow ||
    hasRunningRefresh ||
    (stageInfo?.status !== 'revised' && stageInfo?.status !== 'not_started');

  const tableDataSource = useMemo(() => {
    if (showMore) {
      return samples;
    }
    return samples.slice(0, LOAD_MORE_CUTTOFF_LENGTH);
  }, [showMore, samples]);

  const handleSampleModalClose = () => {
    setSampleModalVisibility(false);
  };

  const handleShowMoreClick = () => {
    setShowMore((v) => !v);
  };

  const handleReportCategoryModalClose = () => {
    setReportCategoryModalVisibility(false);
  };

  const handleReportPanelModalClose = () => {
    setReportPanelModalVisibility(false);
    setSelectedRecord(undefined);
  };

  return (
    <>
      {isReportPanelModalVisible && (
        <ReportPanelModal
          closeModal={handleReportPanelModalClose}
          sample={selectedRecord}
        />
      )}
      {isSubjectModalVisible && (
        <SubjectModal
          closeModal={handleSubjectModalClose}
          sample={selectedRecord}
        />
      )}

      {isSampleModalVisible && (
        <SampleModal closeModal={handleSampleModalClose} />
      )}

      {isSampleEditModalVisible && (
        <SampleEditModal
          closeModal={handleSampleEditModalClose}
          sample={selectedRecord}
          handleSubjectModalOpen={handleSubjectModalOpen}
          handleSubjectModalClose={handleSubjectModalClose}
        />
      )}

      {isReportCategoryModalVisible && reportData && selectedRecord && (
        <ReportCategoryModal
          onClose={handleReportCategoryModalClose}
          reportData={reportData}
          variantSamples={samples}
          selectedRecord={selectedRecord}
          setSelectedRecord={setSelectedRecord}
          setReportData={setReportData}
        />
      )}

      {isZygosityModalVisible && selectedRecord && (
        <ZygosityModal
          onClose={handleZygosityModalClose}
          variantSamples={samples}
          selectedSample={selectedRecord}
        />
      )}

      {isSegregationModalVisible && selectedRecord && (
        <SegregationModal
          onClose={handleSegregationModalClose}
          variantSamples={samples}
          selectedSample={selectedRecord}
        />
      )}

      {isMISelectVisible &&
        selectedRecord &&
        selectedRecord.report_data?.length > 1 && (
          <MedicalInvestigationSelectModal
            open={isMISelectVisible}
            onCancel={() => {
              setMISelectVisible(false);
              setSelectedRecord(undefined);
              setPendingReportType(undefined);
            }}
            reportDataList={selectedRecord.report_data}
            onConfirm={async (medicalinvestigation_id) => {
              if (!selectedRecord || !pendingReportType) {
                return;
              }
              const ok = await generateReport(
                selectedRecord,
                pendingReportType,
                medicalinvestigation_id,
              );
              if (ok) {
                setMISelectVisible(false);
                setSelectedRecord(undefined);
                setPendingReportType(undefined);
              }
            }}
          />
        )}

      <div className="clinical-obs__probadn-header">
        <h3>Probands</h3>
        <Button
          type="primary"
          icon={<FontAwesomeIcon icon={faPlus} />}
          className="clinical-obs__add-proband-btn"
          onClick={() => setSampleModalVisibility(true)}
          disabled={isNewSampleDisabled}
        >
          New sample
        </Button>
      </div>
      <ResizableTable
        columns={columns}
        dataSource={tableDataSource}
        pagination={false}
        loading={isSamplesLoading}
        className="clinical-obs__proband-table"
        rowKey={(record) => record.sample_external_id}
        persistenceKey="probands"
      />
      {samples.length > LOAD_MORE_CUTTOFF_LENGTH && (
        <LoadMoreBtn
          showMore={showMore}
          handleShowMoreClick={handleShowMoreClick}
        />
      )}
    </>
  );
};

export default SamplesSection;
